import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Mail, Phone, Briefcase, Save, X, Loader2, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useGetTeamMemberByIdQuery, useUpdateTeamMemberMutation } from '../../store/teamMemberApi';
import { toast } from '@clareefai/ui';
import ConfirmEmailChangeDialog from '../../components/teams/ConfirmEmailChangeDialog';

const EditTeamMember: React.FC = () => {
  const { t } = useTranslation();
  const { memberId } = useParams<{ memberId: string }>();
  const navigate = useNavigate();
  const [updateTeamMember, { isLoading: isUpdating }] = useUpdateTeamMemberMutation();

  const { data: memberData, isLoading } = useGetTeamMemberByIdQuery(memberId || '', {
    skip: !memberId,
  });

  const [formData, setFormData] = useState<{
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    role: string;
  } | null>(null);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [pendingPayload, setPendingPayload] = useState<Record<string, string> | null>(null);

  // Initialize form data once member loads
  const member = memberData?.teamMember;
  if (member && !formData) {
    setFormData({
      firstName: member.firstName,
      lastName: member.lastName,
      email: member.email,
      phone: member.phone || '',
      role: member.role,
    });
  }

  const roles = [
    { id: 'Marketing Manager', labelKey: 'teams.roles.marketingManager' },
    { id: 'Manager', labelKey: 'teams.roles.manager' },
    { id: 'Account Manager', labelKey: 'teams.roles.accountManager' },
    { id: 'Customer Success', labelKey: 'teams.roles.customerSuccess' },
    { id: 'Account Executive', labelKey: 'teams.roles.accountExecutive' },
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => prev ? { ...prev, [name]: value } : prev);
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    if (!formData) return false;
    const newErrors: { [key: string]: string } = {};

    if (!formData.firstName.trim()) newErrors.firstName = t('teams.add.firstNameRequired');
    if (!formData.lastName.trim()) newErrors.lastName = t('teams.add.lastNameRequired');
    if (!formData.email.trim()) {
      newErrors.email = t('teams.add.emailRequired');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('teams.add.emailInvalid');
    }
    if (!formData.role) newErrors.role = t('teams.add.roleRequired');

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const submitPayload = async (data: Record<string, string>) => {
    if (!memberId) return;
    const emailChanged = 'email' in data;
    try {
      await updateTeamMember({ id: memberId, data }).unwrap();
      toast.success(
        emailChanged
          ? t('teams.edit.updateSuccessEmail', { email: data.email })
          : t('teams.edit.updateSuccess')
      );
      setPendingPayload(null);
      navigate(`/team-member/${memberId}`);
    } catch (err: any) {
      console.error('Failed to update team member:', err);
      toast.error(err?.data?.message || t('teams.edit.updateError'));
      setPendingPayload(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!memberId || !formData || !member || !validateForm()) return;

    // Build payload with only changed fields
    const data: Record<string, string> = {};
    if (formData.firstName !== member.firstName) data.firstName = formData.firstName;
    if (formData.lastName !== member.lastName) data.lastName = formData.lastName;
    if (formData.email !== member.email) data.email = formData.email;
    if (formData.phone !== (member.phone || '')) data.phone = formData.phone;
    if (formData.role !== member.role) data.role = formData.role;

    if (Object.keys(data).length === 0) {
      toast.info(t('teams.edit.noChanges'));
      return;
    }

    if ('email' in data) {
      setPendingPayload(data);
      return;
    }

    await submitPayload(data);
  };

  const handleCancel = () => {
    navigate(`/team-member/${memberId}`);
  };

  if (isLoading || !formData) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="w-12 h-12 animate-spin text-[#4339A0]" />
          <p className="text-gray-600">{t('teams.edit.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <div className="bg-gradient-to-r from-[#4339A0] via-[#5a4dba] to-[#3d2f8f] text-white">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <button
            onClick={handleCancel}
            className="mb-6 flex items-center space-x-2 text-[#b8b0f0] hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>{t('teams.edit.backToDetails')}</span>
          </button>

          <div>
            <h1 className="text-4xl font-bold mb-2">{t('teams.edit.heading')}</h1>
            <p className="text-xl text-[#b8b0f0]">{t('teams.edit.subtitle', { name: member?.name ?? '' })}</p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="bg-white rounded-xl shadow-sm">
          <form onSubmit={handleSubmit}>
            <div className="p-8 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center space-x-2">
                    <User className="w-4 h-4" />
                    <span>{t('teams.add.firstName')}</span>
                  </div>
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder={t('teams.add.firstNamePlaceholder')}
                  disabled={isUpdating}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#4339A0] focus:border-transparent transition-colors ${
                    errors.firstName ? 'border-red-500' : 'border-gray-300'
                  } ${isUpdating ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                />
                {errors.firstName && (
                  <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center space-x-2">
                    <User className="w-4 h-4" />
                    <span>{t('teams.add.lastName')}</span>
                  </div>
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder={t('teams.add.lastNamePlaceholder')}
                  disabled={isUpdating}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#4339A0] focus:border-transparent transition-colors ${
                    errors.lastName ? 'border-red-500' : 'border-gray-300'
                  } ${isUpdating ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                />
                {errors.lastName && (
                  <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center space-x-2">
                    <Mail className="w-4 h-4" />
                    <span>{t('teams.add.email')}</span>
                  </div>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={t('teams.add.emailPlaceholder')}
                  disabled={isUpdating}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#4339A0] focus:border-transparent transition-colors ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  } ${isUpdating ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                )}
                <div className="mt-2 flex items-start space-x-2 text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                  <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{t('teams.edit.emailChangeInfo')}</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center space-x-2">
                    <Phone className="w-4 h-4" />
                    <span>{t('teams.edit.phone')}</span>
                  </div>
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder={t('teams.edit.phonePlaceholder')}
                  disabled={isUpdating}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#4339A0] focus:border-transparent transition-colors border-gray-300 ${
                    isUpdating ? 'bg-gray-100 cursor-not-allowed' : ''
                  }`}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center space-x-2">
                    <Briefcase className="w-4 h-4" />
                    <span>{t('teams.add.role')}</span>
                  </div>
                </label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  disabled={isUpdating}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#4339A0] focus:border-transparent transition-colors ${
                    errors.role ? 'border-red-500' : 'border-gray-300'
                  } ${isUpdating ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                >
                  <option value="">{t('teams.add.selectRole')}</option>
                  {roles.map(role => (
                    <option key={role.id} value={role.id}>
                      {t(role.labelKey)}
                    </option>
                  ))}
                </select>
                {errors.role && (
                  <p className="mt-1 text-sm text-red-600">{errors.role}</p>
                )}
              </div>

              <div className="pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600 mb-4">{t('teams.add.requiredFields')}</p>
                <div className="flex items-center justify-end space-x-3">
                  <button
                    type="button"
                    onClick={handleCancel}
                    disabled={isUpdating}
                    className={`flex items-center space-x-2 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg transition-colors ${
                      isUpdating ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'
                    }`}
                  >
                    <X className="w-4 h-4" />
                    <span>{t('common.cancel')}</span>
                  </button>
                  <button
                    type="submit"
                    disabled={isUpdating}
                    className={`flex items-center space-x-2 px-6 py-3 bg-[#4339A0] text-white rounded-lg transition-colors ${
                      isUpdating ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#362d85]'
                    }`}
                  >
                    {isUpdating ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>{t('teams.edit.saving')}</span>
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        <span>{t('common.saveChanges')}</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      <ConfirmEmailChangeDialog
        isOpen={pendingPayload !== null}
        onCancel={() => setPendingPayload(null)}
        onConfirm={() => {
          if (pendingPayload) submitPayload(pendingPayload);
        }}
        isLoading={isUpdating}
        currentEmail={member?.email || ''}
        newEmail={pendingPayload?.email || ''}
      />
    </div>
  );
};

export default EditTeamMember;
